import React from "react";
import { Box, IconButton, Modal, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import EmailIcon from "@mui/icons-material/Email";
import { Colors } from "../../../configs";
import { pxToRem } from "../../../util";
import { buildWaiverHtml } from "../WaiverPrint/WaiverPrintPage";

interface AddressWaiverViewModalProps {
  open: boolean;
  onClose: () => void;
  customerName: string;
  customerPhone: string;
  signatureDataUrl?: string;
}

const AddressWaiverViewModal: React.FC<AddressWaiverViewModalProps> = ({
  open,
  onClose,
  customerName,
  customerPhone,
  signatureDataUrl,
}) => {
  const waiverHtml = buildWaiverHtml({ customerName, customerPhone, signatureDataUrl });

  const handlePrint = () => {
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    const printHtml = waiverHtml.replace(
      "</body>",
      `<script>
        window.addEventListener('load', function () {
          setTimeout(function(){ window.print(); }, 100);
        });
        window.addEventListener('afterprint', function () { window.close(); });
      </script>
    </body>`
    );
    document.body.appendChild(iframe);
    iframe.contentDocument?.open();
    iframe.contentDocument?.write(printHtml);
    iframe.contentDocument?.close();
    iframe.onload = () => {
      iframe.contentWindow?.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    };
  };

  const handleDownload = () => {
    const blob = new Blob([waiverHtml], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Address_Waiver_${customerName.replace(/\s+/g, "_")}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const actionButtonSx = {
    backgroundColor: Colors.WHITE,
    border: `1px solid ${Colors.PRIMARY}`,
    borderRadius: "0.357rem",
    width: "3rem",
    height: "3rem",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableScrollLock
      disableRestoreFocus
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: pxToRem(1344),
          bgcolor: Colors.WHITE,
          p: 4,
          borderRadius: "0.71rem",
          outline: "none",
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: "0.857rem",
            right: "0.857rem",
            color: Colors.MAIN_GRAY,
          }}
        >
          <CloseIcon sx={{ fontSize: "1.571rem" }} />
        </IconButton>

        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 3 }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              sx={{
                color: Colors.MAIN_GRAY,
                fontSize: "1.857rem",
                fontWeight: "bold",
              }}
            >
              Address Waiver
            </Typography>
            {customerName && (
              <>
                <Typography sx={{ color: Colors.MAIN_GRAY, fontSize: "1.428rem" }}>
                  -
                </Typography>
                <Typography sx={{ color: Colors.MAIN_GRAY, fontSize: "1.428rem" }}>
                  {customerName}
                </Typography>
              </>
            )}
          </Box>

          {/* Action buttons */}
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box sx={actionButtonSx}>
              <IconButton onClick={handleDownload} size="small">
                <DownloadIcon sx={{ fontSize: "1.571rem", color: Colors.PRIMARY }} />
              </IconButton>
            </Box>
            <Box sx={actionButtonSx}>
              <IconButton size="small" disabled>
                <EmailIcon sx={{ fontSize: "1.571rem", color: Colors.PRIMARY }} />
              </IconButton>
            </Box>
            <Box sx={actionButtonSx}>
              <IconButton onClick={handlePrint} size="small">
                <PrintIcon sx={{ fontSize: "1.571rem", color: Colors.PRIMARY }} />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Document viewer */}
        <Box
          sx={{
            width: "100%",
            height: pxToRem(750),
            bgcolor: Colors.BACKSHADE_GRAY,
            borderRadius: "0.5rem",
            overflow: "hidden",
          }}
        >
          <iframe
            srcDoc={waiverHtml}
            style={{ width: "100%", height: "100%", border: "none" }}
            title="Address Waiver Document"
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default AddressWaiverViewModal;
